import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { resolveHref } from 'next/dist/client/resolve-href';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useWindowSize } from 'src/hooks';
import {
  BackHeader,
  Button,
  Icon,
  Loader,
  Modal,
  PageContentWrapper,
  RowSelector,
  SkipToContentTarget,
  Switch,
} from 'src/components';
import { MeApi, SettingsApi, StreakApi } from 'src/api';
import { MAX_STREAK, MIN_STREAK } from 'src/constants/streak.const';
import NotificationContext from 'src/context/notification';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { PlatformPage } from 'src/utils/analytics/analytics.const';
import { GetLearningSettingsResponse } from 'src/api/settings/types';
import { UserLanguagesResponse } from 'src/api/me/types';
import { ONE_MINUTE } from 'src/constants/times.const';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { getAnalyticsLanguageInfo } from 'src/utils/analytics/getAnalyticsLanguageInfo';
import { getAnalyticsUserInfo } from 'src/utils/analytics/getAnalyticsUserInfo';
import DashboardFrame from 'src/apps/DashboardFrame';
import Notification from '../Tools/Notification';
import { ConfirmationPopup } from '../Languages/ConfirmationPopup';
import tappingTestScreenshot from './assets/tappingTestScreenshot.png';
import typingTestScreenshot from './assets/typingTestScreenshot.png';
import mobileTappingTestScreenshot from './assets/mobileTappingTestScreenshot.png';
import mobileTypingTestScreenshot from './assets/mobileTypingTestScreenshot.png';
import * as S from './styles';

const msgs = defineMessages({
  title: { id: 'web_ls_title' },
  currentLanguageHint: {
    id: 'learningsettings_body_copy',
    defaultMessage: 'All settings will apply to this language only',
  },
  changeLanguageCta: {
    id: 'learningsettings_languageselector_cta',
    defaultMessage: 'Change',
  },
  wordsPerSession: { id: 'web_ls_subheader_length' },
  testTypePreferences: { id: 'web_ls_subheader_tests' },
  learning: {
    id: 'learningsettings_subtitle_learning',
    defaultMessage: 'Learning',
  },
  review: { id: 'web_ls_review' },
  speedReview: { id: 'web_ls_speedreview' },
  difficult: { id: 'web_ea_activity_dw' },
  prioritise: { id: 'web_ls_typing_prioritise' },
  prioritiseDescription: { id: 'web_ls_typing_prioritise_desc' },
  jumble: {
    id: 'learningsettings_testtype_preferences_subtitle1',
    defaultMessage: 'Arrange the words tests',
  },
  jumbleDescription: { id: 'web_ls_jumble_desc' },
  tappingTestModalHeader: {
    id: 'learningsettings_testtype_preferences_example1',
    defaultMessage: 'Arrange the words test example',
  },
  audio: { id: 'settings_learningSettings_title_audioTests' },
  audioDescription: { id: 'settings_learningSettings_description_audioTests' },
  typing: { id: 'settings_learningSettings_title_typingTests' },
  typingDescription: { id: 'settings_learningSettings_description_typingTests' },
  typingTestModalHeader: { id: 'web_ls_typing_example' },
  streaksSettingsTitle: { id: 'streaks_settings_title' },
  streakInDays: { id: 'streaks_settings_length' },
  updateStreak: { id: 'streaks_settings_update' },
  errorStreak: { id: 'streaks_settings_error_max' },
  streaksSettingsSuccess: { id: 'streaks_settings_success' },
  saveSettingsError: {
    id: 'learningsettings_errormessage',
    defaultMessage: "Your settings can't be saved, please try again ",
  },
  offline: { id: 'beta_tab_offline_title' },
  learningOptions: {
    id: 'learningsettings_header_learningoptions',
    defaultMessage: 'Learning Options',
  },
  useRomanization: { id: 'settings_learningSettings_title_romanization' },
  useRomanizationDescription: { id: 'settings_learningSettings_description_romanization' },
  deleteLanguageTitle: { id: 'resetprogress_language_selector_deletebin' },
  deleteLanguageDescription: { id: 'resetprogress_delete_language_bodycopy' },
  deleteLanguageCta: { id: 'resetprogress_language_selector_deletebin' },
});

export const Settings = () => {
  const intl = useIntl();
  const { user } = useUserContext();
  const { width } = useWindowSize();
  const { setNotification } = useContext(NotificationContext);
  const languagePairId = getLanguagePairId();
  const router = useRouter();
  const { safePush } = useSafePush();

  const [showTappingTestModal, setShowTappingTestModal] = useState<boolean>(false);
  const [showTypingTestModal, setShowTypingTestModal] = useState<boolean>(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);

  const [newStreak, setNewStreak] = useState<number | null>(null);
  const isNewStreakValid =
    !!newStreak && Number(newStreak) >= MIN_STREAK && Number(newStreak) <= MAX_STREAK;

  const isMobileSize = useMemo(() => width < 640, [width]); // 640px = theme.sz.md

  const { data: userLanguagesData } = useQuery<UserLanguagesResponse>({
    queryKey: ['userLanguages'],
    queryFn: () => {
      return MeApi.getUserLanguages();
    },
    staleTime: ONE_MINUTE,
  });

  const currentLP = userLanguagesData?.languagePairs.find(
    languagePair => languagePair.languagePairId === languagePairId,
  );

  const {
    data: settings,
    isLoading,
    refetch,
  } = useQuery<GetLearningSettingsResponse | null>({
    queryKey: ['learningSettings', languagePairId],

    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      return SettingsApi.getLearningSettings({ language_pair_id: languagePairId });
    },
  });

  const { mutate: saveLearningSettings, isError: isSaveError } = useMutation({
    mutationFn: async ({
      parameter,
      value,
    }: {
      parameter: string;
      value: string | number | boolean;
    }) => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return;
      await SettingsApi.saveLearningSettings({
        [parameter]: value,
        language_pair_id: languagePairId,
      });
    },

    onSuccess: async () => {
      refetch()
        .then(({ data: newSettings }) => {
          window.dataLayer.push({
            event: 'change_learning_settings',
            page_screen_type: 'Learning settings',
            page_screen_name: 'Change learning settings',
            profile_setting_learn: `${newSettings?.wordsPerSession.learn}`,
            profile_setting_review: `${newSettings?.wordsPerSession.review}`,
            profile_setting_difficult: `${newSettings?.wordsPerSession.difficult}`,
            profile_setting_speed_review: `${newSettings?.wordsPerSession.speed}`,
            profile_setting_jumbled_word_tests: `${!newSettings?.testTypes.tappingDisabled}`,
            profile_setting_prioritise_typing_tests_in_review: `${newSettings?.testTypes.priorityForTyping}`,
            profile_setting_disable_audio_tests: `${newSettings?.testTypes.disableMultimedia}`,
            profile_setting_show_romanization: `${newSettings?.showRomanization}`,
            ...getAnalyticsLanguageInfo(intl),
            ...getAnalyticsUserInfo(user),
          });
        })
        .catch(() => {
          /* no-floating-promises */
        });
    },
  });

  const handleChange = useCallback(
    (parameter: string) => async (value: string | boolean | number) => {
      if (!navigator.onLine) {
        setNotification({
          text: intl.formatMessage(msgs.offline),
          status: 'warning',
          snackBar: true,
        });
      }

      saveLearningSettings({ parameter, value });
    },
    [saveLearningSettings, setNotification, intl],
  );

  const screenshotModal = (handleClose: () => void, title: string, imageSrc: string) => (
    <Modal isOpen darkCloseIcon handleClose={handleClose}>
      <S.ModalContent>
        <S.ModalHeader>{title}</S.ModalHeader>
        <S.ImageWrapper>
          <S.Image $src={imageSrc} />
        </S.ImageWrapper>
      </S.ModalContent>
    </Modal>
  );

  const tappingTestModal = screenshotModal(
    () => setShowTappingTestModal(!showTappingTestModal),
    intl.formatMessage(msgs.tappingTestModalHeader),
    isMobileSize
      ? /* istanbul ignore next: mobile version */ mobileTappingTestScreenshot
      : tappingTestScreenshot,
  );
  const typingTestModal = screenshotModal(
    () => setShowTypingTestModal(!showTypingTestModal),
    intl.formatMessage(msgs.typingTestModalHeader),
    isMobileSize
      ? /* istanbul ignore next: mobile version */ mobileTypingTestScreenshot
      : typingTestScreenshot,
  );

  const changeLanguageHref = resolveHref(router, {
    pathname: ROUTES.languages,
    query: {
      next: ROUTES.settings.learning,
    },
  });

  useEffect(() => {
    if (isSaveError) {
      setNotification({
        text: intl.formatMessage(msgs.saveSettingsError),
        status: 'error',
        snackBar: true,
      });
    }
  }, [isSaveError, setNotification, intl]);

  if (isLoading || !languagePairId) {
    return (
      <S.LoaderContainer>
        <Loader />
      </S.LoaderContainer>
    );
  }

  return (
    <>
      {showTappingTestModal && tappingTestModal}
      {showTypingTestModal && typingTestModal}
      <BackHeader href={ROUTES.dashboard} />
      <SkipToContentTarget />
      <PageContentWrapper>
        <Notification />
        <S.Content>
          <S.Header>{intl.formatMessage(msgs.title)}</S.Header>

          {currentLP && (
            <S.CurrentLanguageContainer onClick={() => safePush(changeLanguageHref)}>
              <S.CurrentLanguageFlag>
                <S.CurrentLanguageFlagImage src={currentLP.targetLanguageFlagImage} alt="" />
              </S.CurrentLanguageFlag>
              <S.CurrentLanguageName>{currentLP.targetLanguageName}</S.CurrentLanguageName>
              <S.CurrentLanguageHint>
                {intl.formatMessage(msgs.currentLanguageHint)}
              </S.CurrentLanguageHint>
              <S.ChangeLanguageButton
                a11yText={intl.formatMessage(msgs.changeLanguageCta)}
                buttonType="tertiary"
                href={changeLanguageHref}
                data-testId="settings_change_language"
              >
                <Icon name="next" />
              </S.ChangeLanguageButton>
            </S.CurrentLanguageContainer>
          )}

          {settings?.showRomanization !== null && (
            <>
              <S.SecondaryHeader>{intl.formatMessage(msgs.learningOptions)}</S.SecondaryHeader>
              <S.SettingsBlock>
                <S.SwitchRow>
                  <S.SwitchRowTitle>{intl.formatMessage(msgs.useRomanization)}</S.SwitchRowTitle>
                  <S.SwitchRowDescription>
                    {intl.formatMessage(msgs.useRomanizationDescription)}
                  </S.SwitchRowDescription>
                  <S.SwitchRowSwitch>
                    <Switch
                      data-testid="use-romanization"
                      aria-label={intl.formatMessage(msgs.useRomanization)}
                      checked={!!settings?.showRomanization}
                      onChange={async () => {
                        await handleChange('show_romanization')(!settings?.showRomanization);
                      }}
                    />
                  </S.SwitchRowSwitch>
                </S.SwitchRow>
              </S.SettingsBlock>
            </>
          )}

          <S.SecondaryHeader>{intl.formatMessage(msgs.streaksSettingsTitle)}</S.SecondaryHeader>
          <S.StreakWrapper>
            <S.Input
              data-testid="streak-input"
              value={typeof newStreak === 'number' ? newStreak : ''}
              placeholder={intl.formatMessage(msgs.streakInDays)}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const newValue = e?.target.value;

                if (!newValue) {
                  setNewStreak(null);
                }

                const streakNumber = parseInt(newValue.replace('-', ''));

                if (!Number.isNaN(streakNumber)) {
                  setNewStreak(streakNumber);
                }
              }}
              type="number"
              min={MIN_STREAK}
              max={MAX_STREAK}
              error={!newStreak || isNewStreakValid ? null : intl.formatMessage(msgs.errorStreak)}
            />
            <S.StreakButtonWrapper>
              <Button
                disabled={!isNewStreakValid}
                buttonType="primary"
                buttonSize="large"
                data-testId="settings_change_streak"
                onClick={async () => {
                  await StreakApi.postStreak(languagePairId, {
                    count: Number(newStreak),
                  }).then(() =>
                    setNotification({
                      text: intl.formatMessage(msgs.streaksSettingsSuccess),
                      status: 'success',
                      snackBar: true,
                    }),
                  );
                }}
              >
                {intl.formatMessage(msgs.updateStreak)}
              </Button>
            </S.StreakButtonWrapper>
          </S.StreakWrapper>

          <S.SecondaryHeader>{intl.formatMessage(msgs.wordsPerSession)}</S.SecondaryHeader>
          <S.SettingsBlock>
            <S.SettingsRowWrapper>
              <RowSelector
                title={intl.formatMessage(msgs.learning)}
                testIdPrefix="settings_learn_session"
                options={[3, 5, 7, 10]}
                selectedOption={settings?.wordsPerSession.learn}
                onChange={handleChange('words_per_learn_session')}
              />
            </S.SettingsRowWrapper>
            <S.Divider />
            <S.SettingsRowWrapper>
              <RowSelector
                title={intl.formatMessage(msgs.review)}
                testIdPrefix="settings_review_session"
                options={[5, 10, 15, 25, 50]}
                selectedOption={settings?.wordsPerSession.review}
                onChange={handleChange('words_per_review_session')}
              />
            </S.SettingsRowWrapper>
            <S.Divider />
            <S.SettingsRowWrapper>
              <RowSelector
                title={intl.formatMessage(msgs.speedReview)}
                testIdPrefix="settings_speed_session"
                options={[10, 25, 50, 100, 150]}
                selectedOption={settings?.wordsPerSession.speed}
                onChange={handleChange('words_per_speed_session')}
              />
            </S.SettingsRowWrapper>
            <S.Divider />
            <S.SettingsRowWrapper>
              <RowSelector
                title={intl.formatMessage(msgs.difficult)}
                testIdPrefix="settings_difficult_session"
                options={[5, 10, 15, 20]}
                selectedOption={settings?.wordsPerSession.difficult}
                onChange={handleChange('words_per_difficult_session')}
              />
            </S.SettingsRowWrapper>
          </S.SettingsBlock>

          <S.SecondaryHeader>{intl.formatMessage(msgs.testTypePreferences)}</S.SecondaryHeader>
          <S.SettingsBlock>
            <S.SwitchRow>
              <S.SwitchRowTitle>
                {intl.formatMessage(msgs.jumble)}
                <S.InfoButton
                  data-testid="jumbledWordTest-icon"
                  aria-label={intl.formatMessage(msgs.tappingTestModalHeader)}
                  onClick={() => setShowTappingTestModal(true)}
                >
                  <Icon name="infoOutline" />
                </S.InfoButton>
              </S.SwitchRowTitle>
              <S.SwitchRowDescription>
                {intl.formatMessage(msgs.jumbleDescription)}
              </S.SwitchRowDescription>
              <S.SwitchRowSwitch>
                <Switch
                  data-testid="tappingTestDisabled"
                  aria-label={intl.formatMessage(msgs.jumble)}
                  checked={!settings?.testTypes.tappingDisabled}
                  onChange={async () => {
                    await handleChange('tapping_test_disabled')(
                      !settings?.testTypes.tappingDisabled,
                    );
                  }}
                />
              </S.SwitchRowSwitch>
            </S.SwitchRow>
            <S.Divider />
            <S.SwitchRow>
              <S.SwitchRowTitle>
                {intl.formatMessage(msgs.typing)}
                <S.InfoButton
                  data-testid="typingTests-icon"
                  aria-label={intl.formatMessage(msgs.typingTestModalHeader)}
                  onClick={() => setShowTypingTestModal(true)}
                >
                  <Icon name="infoOutline" />
                </S.InfoButton>
              </S.SwitchRowTitle>
              <S.SwitchRowDescription>
                {intl.formatMessage(msgs.typingDescription)}
              </S.SwitchRowDescription>
              <S.SwitchRowSwitch>
                <Switch
                  data-testid="typingTest"
                  aria-label={intl.formatMessage(msgs.typing)}
                  checked={!settings?.testTypes.disableTyping}
                  onChange={async () => {
                    await handleChange('disable_typing')(!settings?.testTypes.disableTyping);
                  }}
                />
              </S.SwitchRowSwitch>
            </S.SwitchRow>
            <S.Divider />
            <S.SwitchRow>
              <S.SwitchRowTitle>
                {intl.formatMessage(msgs.prioritise)}
                <S.InfoButton
                  data-testid="prioritiseTypingTests-icon"
                  aria-label={intl.formatMessage(msgs.typingTestModalHeader)}
                  onClick={() => setShowTypingTestModal(true)}
                >
                  <Icon name="infoOutline" />
                </S.InfoButton>
              </S.SwitchRowTitle>
              <S.SwitchRowDescription>
                {intl.formatMessage(msgs.prioritiseDescription)}
              </S.SwitchRowDescription>
              <S.SwitchRowSwitch>
                <Switch
                  data-testid="priorityForTyping"
                  aria-label={intl.formatMessage(msgs.prioritise)}
                  checked={settings?.testTypes.priorityForTyping}
                  onChange={async () => {
                    await handleChange('priority_for_typing')(
                      !settings?.testTypes.priorityForTyping,
                    );
                  }}
                />
              </S.SwitchRowSwitch>
            </S.SwitchRow>
            <S.Divider />
            <S.SwitchRow>
              <S.SwitchRowTitle>{intl.formatMessage(msgs.audio)}</S.SwitchRowTitle>
              <S.SwitchRowDescription>
                {intl.formatMessage(msgs.audioDescription)}
              </S.SwitchRowDescription>
              <S.SwitchRowSwitch>
                <Switch
                  data-testid="audio-tests"
                  aria-label={intl.formatMessage(msgs.audio)}
                  checked={!settings?.testTypes.disableMultimedia}
                  onChange={async () => {
                    await handleChange('disable_multimedia')(
                      !settings?.testTypes.disableMultimedia,
                    );
                  }}
                />
              </S.SwitchRowSwitch>
            </S.SwitchRow>
          </S.SettingsBlock>

          {currentLP && (
            <>
              <S.DeleteLanguageHeader>
                {intl.formatMessage(msgs.deleteLanguageTitle)}
              </S.DeleteLanguageHeader>
              <S.DeleteLanguageRow>
                <S.DeleteLanguageDescription>
                  {intl.formatMessage(msgs.deleteLanguageDescription)}
                </S.DeleteLanguageDescription>
                <Button
                  buttonType="secondary"
                  buttonSize="medium"
                  wide
                  data-testId="settings_delete_account"
                  onClick={() => setShowDeleteConfirmationModal(true)}
                >
                  {intl.formatMessage(msgs.deleteLanguageCta)}
                </Button>
              </S.DeleteLanguageRow>
              <ConfirmationPopup
                isOpen={showDeleteConfirmationModal}
                type="delete"
                flag={currentLP.targetLanguageFlagImage}
                onAction={() => {
                  void safePush(
                    resolveHref(router, {
                      pathname: ROUTES.languages,
                      query: { autoDeleteLP: currentLP.languagePairId },
                    }),
                  );
                }}
                onClose={() => setShowDeleteConfirmationModal(false)}
              />
            </>
          )}
        </S.Content>
      </PageContentWrapper>
    </>
  );
};

const LearningSettings = () => {
  const intl = useIntl();

  return (
    <DashboardFrame
      hasSidebar={false}
      pageScreenName={PlatformPage.LEARNING}
      pageTitle={intl.formatMessage(msgs.title)}
    >
      <Settings />
    </DashboardFrame>
  );
};

export default LearningSettings;
